import { Controller } from "@hotwired/stimulus"

// MS50 #3196: keeps file bytes off the WAF-protected application request.
// Ready-token purposes submit only after the trusted sanitizer/finalizer reports
// ready. #3307 async purposes submit after the quarantine receipt is durable;
// file inputs are replaced by signed upload-session tokens in both paths.
export default class extends Controller {
  static values = { createUrl: String, telemetryUrl: String }

  static telemetryS3Codes = [
    "AccessDenied",
    "EntityTooLarge",
    "ExpiredToken",
    "SignatureDoesNotMatch",
    "other"
  ]

  static terminalUploadStates = [
    "rejected",
    "failed",
    "cancelled",
    "superseded",
    "expired"
  ]

  connect() {
    this.bypass = false
    this.busy = false
  }

  async submit(event) {
    if (this.bypass) return

    const selected = this.selectedFiles()
    if (selected.length === 0) {
      if (event.submitter?.dataset.requireUploadPurpose) {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.showError("Choose a file before uploading.")
      }
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    if (this.busy) return

    this.busy = true
    const submitter = event.submitter
    this.setSubmitting(true)
    this.clearError()

    try {
      for (const selection of selected) {
        const token = await this.uploadAndWait(selection)
        this.insertToken(selection.input, token)
      }

      selected.forEach(({ input }) => { input.disabled = true })
      this.bypass = true
      this.setSubmitting(false)
      this.element.requestSubmit(submitter || undefined)
    } catch (error) {
      this.element.querySelectorAll("[data-sanitized-upload-token]").forEach((input) => input.remove())
      this.showError(error.message || "Upload failed. Please try again.")
      this.setSubmitting(false)
      this.busy = false
    }
  }

  selectedFiles() {
    return Array.from(this.element.querySelectorAll("input[type='file'][data-upload-purpose]"))
      .flatMap((input) => Array.from(input.files || []).map((file) => ({ input, file })))
  }

  async uploadAndWait({ input, file }) {
    const prepared = await this.prepareUpload(input, file)
    const uploadItem = this.buildUploadItem(input, file)
    const body = new FormData()
    Object.entries(prepared.fields).forEach(([name, value]) => body.append(name, value))
    body.append("file", file)

    const uploaded = await this.uploadToStorage(prepared.upload_url, body, uploadItem)
    if (!uploaded.ok) {
      // MS50 #3255: surface the HTTP status and the S3 <Error><Code> instead of
      // swallowing every failure behind one opaque string. During the #3255
      // outage every direct upload 403'd (AccessDenied) and it was invisible.
      const status = uploaded.status
      const responseBody = uploaded.responseText || ""
      const code = this.parseS3ErrorCode(responseBody)
      const detail = code ? `${status} ${code}` : `${status}`
      this.sendFailureTelemetry("s3_post", status, this.telemetryS3Code(code), prepared.token)
      throw new Error(`The file could not be uploaded to secure storage (${detail}).`)
    }

    this.markUploadQueued(uploadItem)
    if (prepared.acceptance === "async" && prepared.receipt_url) {
      await this.recordReceipt(prepared.receipt_url, prepared.token)
    } else if (prepared.acceptance !== "async") {
      await this.waitUntilReady(prepared.status_url, prepared.token)
    }
    return prepared.token
  }

  chooseActionFile(event) {
    if (this.busy) return

    const input = this.element.querySelector("[data-sanitized-upload-action-file]")
    if (!input) return

    input.dataset.uploadAction = event.currentTarget.dataset.uploadAction
    input.value = ""
    input.click()
  }

  async actionFileSelected(event) {
    const input = event.currentTarget
    const file = input.files?.[0]
    if (!file || this.busy) return

    this.busy = true
    this.setActionBusy(true)
    this.clearError()
    try {
      await this.uploadAndWait({ input, file })
      window.location.reload()
    } catch (error) {
      this.showError(error.message || "Upload failed. Please try again.")
      input.value = ""
      this.setActionBusy(false)
      this.busy = false
    }
  }

  async cancelAction(event) {
    event.preventDefault()
    event.stopImmediatePropagation()
    if (this.busy) return
    if (!window.confirm("Cancel this pending upload?")) return

    const form = event.currentTarget
    const token = new FormData(form).get("token")
    this.busy = true
    this.setActionBusy(true)
    this.clearError()
    try {
      const response = await fetch(
        form.action,
        {
          method: "DELETE",
          credentials: "same-origin",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
          },
          body: JSON.stringify({ token })
        }
      )
      if (!response.ok) throw new Error("The upload could not be cancelled.")
      window.location.reload()
    } catch (error) {
      this.showError(error.message || "The upload could not be cancelled.")
      this.setActionBusy(false)
      this.busy = false
    }
  }

  uploadToStorage(url, body, uploadItem) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest()
      request.upload.addEventListener("progress", (event) => {
        this.updateUploadProgress(uploadItem, event)
      })
      request.addEventListener("load", () => {
        resolve({
          ok: request.status >= 200 && request.status < 300,
          status: request.status,
          responseText: request.responseText
        })
      })
      request.addEventListener("error", () => {
        reject(new Error("The file could not be uploaded to secure storage (network error)."))
      })
      request.addEventListener("abort", () => {
        reject(new Error("The secure file upload was cancelled."))
      })
      request.open("POST", url)
      request.send(body)
    })
  }

  buildUploadItem(input, file) {
    const container = this.element.querySelector("[data-sanitized-upload-items]") ||
      this.element
    const item = document.createElement("div")
    item.className = "alert alert-info"
    item.dataset.sanitizedUploadItem = "true"
    item.dataset.uploadFilename = file.name
    item.dataset.uploadPurpose = input.dataset.uploadPurpose
    item.dataset.uploadStage = "uploading"

    const identity = document.createElement("strong")
    identity.dataset.uploadFilename = "true"
    identity.textContent = file.name
    item.appendChild(identity)

    const announcement = document.createElement("div")
    announcement.dataset.uploadStageAnnouncement = "true"
    announcement.setAttribute("role", "status")
    announcement.setAttribute("aria-live", "polite")
    announcement.setAttribute("aria-atomic", "true")
    announcement.textContent = "Uploading to secure storage."
    item.appendChild(announcement)

    const progressContainer = document.createElement("div")
    progressContainer.dataset.uploadProgressContainer = "true"
    progressContainer.setAttribute("aria-live", "off")
    item.appendChild(progressContainer)

    container.appendChild(item)
    return item
  }

  updateUploadProgress(uploadItem, event) {
    if (!event.lengthComputable || event.total <= 0) return

    const percentage = Math.max(0, Math.min(100, (event.loaded / event.total) * 100))
    const container = uploadItem.querySelector("[data-upload-progress-container]")
    let progress = container.querySelector("[data-sanitized-upload-progress]")
    if (!progress) {
      progress = document.createElement("progress")
      progress.dataset.sanitizedUploadProgress = "true"
      progress.max = 100
      progress.setAttribute(
        "aria-label",
        `Uploading ${uploadItem.dataset.uploadFilename}`
      )
      container.appendChild(progress)
    }
    progress.value = percentage
  }

  markUploadQueued(uploadItem) {
    uploadItem.dataset.uploadStage = "queued"
    uploadItem.querySelector("[data-upload-progress-container]")?.replaceChildren()
    const announcement = uploadItem.querySelector("[data-upload-stage-announcement]")
    if (announcement) announcement.textContent = "Queued for secure processing."
  }

  // Extract the <Code> from an S3 error XML body (e.g. AccessDenied). Defensive:
  // returns null for an empty or unparseable body so the caller falls back to a
  // status-only message.
  parseS3ErrorCode(body) {
    if (!body) return null
    const match = String(body).match(/<Code>([^<]+)<\/Code>/)
    return match ? match[1].trim() : null
  }

  async prepareUpload(input, file) {
    const purpose = input.dataset.uploadPurpose
    const uploadContext = input.dataset.uploadContext
    const uploadAction = input.dataset.uploadAction || null
    const idempotencyKey = this.idempotencyKeyFor(file, purpose)
    const documentTypeId = purpose === "personal_document"
      ? input.dataset.uploadDocumentTypeId ||
        this.element.querySelector("[name='document_type_id']")?.value || null
      : null
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify({
        purpose,
        upload_context: uploadContext || null,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        byte_size: file.size,
        document_type_id: documentTypeId,
        ...(purpose === "personal_document"
          ? { custom_label: this.element.querySelector("[name='custom_label']")?.value ?? null }
          : {}),
        client_idempotency_key: idempotencyKey,
        upload_action: uploadAction,
        source_token: uploadAction ? this.element.dataset.uploadActionToken : null,
        source_upload_id: uploadAction ? this.element.dataset.uploadId : null
      })
    })
    const payload = await response.json()
    if (!response.ok) throw new Error(payload.error || "The upload could not be prepared.")
    return payload
  }

  idempotencyKeyFor(file, purpose) {
    const input = Array.from(this.element.querySelectorAll("input[type='file'][data-upload-purpose]"))
      .find((candidate) => candidate.dataset.uploadPurpose === purpose &&
        Array.from(candidate.files || []).includes(file))
    const fileIdentity = JSON.stringify([
      file.name,
      file.size,
      file.type,
      file.lastModified,
      input?.dataset.uploadAction || null,
      this.element.dataset.uploadId || null
    ])
    if (input?.dataset.uploadIdempotencyKey &&
        input.dataset.uploadIdempotencyFile === fileIdentity) {
      return input.dataset.uploadIdempotencyKey
    }

    const value = globalThis.crypto?.randomUUID?.() ||
      `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
    if (input) {
      input.dataset.uploadIdempotencyKey = value
      input.dataset.uploadIdempotencyFile = fileIdentity
    }
    return value
  }

  async recordReceipt(receiptUrl, token) {
    const separator = receiptUrl.includes("?") ? "&" : "?"
    const response = await fetch(`${receiptUrl}${separator}token=${encodeURIComponent(token)}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      }
    })
    if (!response.ok) throw new Error("The secure upload receipt could not be recorded.")
  }

  async waitUntilReady(statusUrl, token) {
    let lastSuccessfulStatus = 504
    for (let attempt = 0; attempt < 150; attempt += 1) {
      const response = await fetch(statusUrl, {
        credentials: "same-origin",
        headers: { "Accept": "application/json" }
      })
      let payload = {}
      try {
        payload = await response.json()
      } catch (parseError) {
        payload = {}
      }
      if (!response.ok) {
        this.sendFailureTelemetry("status_poll", response.status, "other", token)
        throw new Error(payload.error || "Upload status could not be checked.")
      }
      lastSuccessfulStatus = response.status
      if (payload.state === "ready") return
      if (this.constructor.terminalUploadStates.includes(payload.state)) {
        this.sendFailureTelemetry("status_poll", response.status, "other", token)
        throw new Error(
          payload.message || "Secure processing could not be completed. Please try again."
        )
      }
      await new Promise((resolve) => window.setTimeout(resolve, 2000))
    }
    this.sendFailureTelemetry("status_poll", lastSuccessfulStatus, "other", token)
    throw new Error("Secure file processing timed out. Please try again.")
  }

  telemetryS3Code(code) {
    return this.constructor.telemetryS3Codes.includes(code) ? code : "other"
  }

  // Best effort only: telemetry must never mask or replace the upload error.
  // The payload is constructed from four bounded values; response XML and user
  // text never enter either transport.
  sendFailureTelemetry(stage, status, code, token) {
    let body
    try {
      body = JSON.stringify({ stage, status, code, token })
      const beaconBody = new Blob([ body ], { type: "application/json" })
      if (navigator.sendBeacon(this.telemetryUrlValue, beaconBody)) return
    } catch (telemetryError) {
      // Fall through to the equally best-effort keepalive transport.
    }

    try {
      fetch(this.telemetryUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true
      }).catch(() => {})
    } catch (telemetryError) {
      // The original user-visible upload failure remains authoritative.
    }
  }

  insertToken(input, token) {
    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = input.name
    hidden.value = token
    hidden.dataset.sanitizedUploadToken = "true"
    this.element.appendChild(hidden)
  }

  setSubmitting(active) {
    this.element.querySelectorAll("button[type='submit'], input[type='submit']").forEach((button) => {
      button.disabled = active
    })
  }

  setActionBusy(active) {
    this.element.querySelectorAll("[data-upload-actions] button").forEach((button) => {
      button.disabled = active
    })
  }

  clearError() {
    this.element.querySelector("[data-sanitized-upload-error]")?.remove()
  }

  showError(message) {
    this.clearError()
    const alert = document.createElement("div")
    alert.className = "alert alert-danger"
    alert.setAttribute("role", "alert")
    alert.dataset.sanitizedUploadError = "true"
    alert.textContent = message
    this.element.prepend(alert)
  }
}
